import { clamp, type V2 } from '../core/math';

/**
 * Phone framing geometry (docs/MOBILE.md "Camera on a phone"). The high camera looks down at `pitch` from
 * `distance` along its view ray, so the ground it shows is not centred on the point it looks at: the top of
 * the screen is a flatter ray that travels further, the bottom a steeper one that lands close. On a short
 * landscape phone the difference is a third of the view, and a car driving down the screen saw 25 m ahead
 * where one driving up saw 32 m. These helpers put the view's centre halfway between the two edges and keep
 * the look-ahead inside a band where the thumbs are not.
 */
export interface ViewExtents {
  /** Metres of ground from the look-at point to the top edge of the screen. */
  up: number;
  /** Metres from the look-at point to the bottom edge. */
  down: number;
}

/** Ground reach of the vertical screen edges for a camera `distance` m from its look-at point. */
export function viewExtents(distance: number, pitchRad: number, fovDeg: number): ViewExtents {
  const half = (fovDeg / 2) * (Math.PI / 180);
  const h = Math.sin(pitchRad) * distance;
  const b = Math.cos(pitchRad) * distance;
  const topRay = pitchRad - half;
  const up = topRay <= 0.02 ? distance * 4 : h / Math.tan(topRay) - b;
  const bottomRay = Math.min(Math.PI / 2, pitchRad + half);
  const down = b - h / Math.tan(bottomRay);
  return { up, down };
}

/**
 * Share of the half-view the focus may be carried towards the screen's top (the mission card and the
 * minimap sit up there) or bottom (the thumbs are in the corners, the middle is free) by the look-ahead.
 */
export const LEAD_BAND_UP = 0.4;
export const LEAD_BAND_DOWN = 0.55;

/**
 * The frame offset (metres, ground plane) that balances the view on a phone: `lead` (where the velocity is
 * taking the frame) with its screen-vertical component held inside the band ({@link LEAD_BAND_UP},
 * {@link LEAD_BAND_DOWN}), plus the shift that puts the focus halfway between the top and bottom edges. `up`
 * is the screen-up unit vector. The focus then sits at the vertical centre at rest, never lower than ~75 %
 * of the screen driving up, never higher than ~28 % driving down (the band is in metres; the top of the
 * screen is further away, so a share of the metres is a bigger share of the pixels), and sees about the
 * same distance ahead both ways.
 */
export function phoneFrameOffset(lead: V2, up: V2, ext: ViewExtents): V2 {
  // Balance: move the centre down-screen by half the difference, so the focus shows (up + down) / 2 both ways.
  const balance = (ext.up - ext.down) / 2;
  const along = lead.x * up.x + lead.z * up.z;
  // The focus sits at `balance - along` from the centre (screen-up positive); keep it inside the band.
  const spot = clamp(balance - along, -LEAD_BAND_DOWN * ext.down, LEAD_BAND_UP * ext.up);
  const keptAlong = balance - spot;
  const sideX = lead.x - up.x * along;
  const sideZ = lead.z - up.z * along;
  return { x: sideX + up.x * (keptAlong - balance), z: sideZ + up.z * (keptAlong - balance) };
}
